import type { AppError } from '@/lib/api/types/errors';
import { ErrorType } from '@/lib/api/types/errors';
import { toast } from '@/shared/hooks/useToast';

/**
 * Toast Utilities
 *
 * Provides convenient helper functions for showing toast notifications
 * for common scenarios (errors, success, warnings, etc.)
 */

export interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
}

/**
 * Default toast durations (in milliseconds)
 */
const DURATION = {
  SHORT: 3000,
  MEDIUM: 5000,
  LONG: 7000,
  PERSISTENT: 10000,
};

/**
 * Shows a success toast notification
 */
export function showSuccessToast(title: string, description?: string, duration?: number) {
  toast({
    title,
    description,
    duration: duration || DURATION.MEDIUM,
    variant: 'success',
  });
}

/**
 * Shows an error toast notification
 */
export function showErrorToast(title: string, description?: string, duration?: number) {
  toast({
    title,
    description,
    duration: duration || DURATION.LONG,
    variant: 'destructive',
  });
}

/**
 * Shows a warning toast notification
 */
export function showWarningToast(title: string, description?: string, duration?: number) {
  toast({
    title: `⚠️ ${title}`,
    description,
    duration: duration || DURATION.MEDIUM,
    variant: 'default',
  });
}

/**
 * Shows an info toast notification
 */
export function showInfoToast(title: string, description?: string, duration?: number) {
  toast({
    title,
    description,
    duration: duration || DURATION.MEDIUM,
    variant: 'default',
  });
}

/**
 * Shows a toast notification from an AppError
 * Automatically determines the appropriate message and severity
 */
export function showErrorToastFromError(error: AppError | Error, customTitle?: string) {
  if ('type' in error && error.type) {
    const appError = error as AppError;

    switch (appError.type) {
      case ErrorType.NETWORK:
        showErrorToast(
          customTitle || 'Network Error',
          'Please check your internet connection and try again.',
          DURATION.LONG,
        );
        break;

      case ErrorType.AUTHENTICATION:
        showErrorToast(
          customTitle || 'Authentication Required',
          'Please log in to continue.',
          DURATION.LONG,
        );
        break;

      case ErrorType.AUTHORIZATION:
        showErrorToast(
          customTitle || 'Access Denied',
          'You do not have permission to perform this action.',
          DURATION.LONG,
        );
        break;

      case ErrorType.NOT_FOUND:
        showErrorToast(
          customTitle || 'Not Found',
          'The requested resource could not be found.',
          DURATION.MEDIUM,
        );
        break;

      case ErrorType.TIMEOUT:
        showErrorToast(
          customTitle || 'Request Timeout',
          'The request took too long. Please try again.',
          DURATION.MEDIUM,
        );
        break;

      case ErrorType.SERVER:
        showErrorToast(
          customTitle || 'Server Error',
          'An error occurred on the server. Please try again later.',
          DURATION.LONG,
        );
        break;

      case ErrorType.VALIDATION:
        showErrorToast(customTitle || 'Validation Error', appError.message, DURATION.MEDIUM);
        break;

      default:
        showErrorToast(
          customTitle || 'Error',
          appError.message || 'An unexpected error occurred.',
          DURATION.MEDIUM,
        );
    }
  } else {
    // Generic error
    showErrorToast(
      customTitle || 'Error',
      error.message || 'An unexpected error occurred.',
      DURATION.MEDIUM,
    );
  }
}

/**
 * Shows a loading toast (can be updated later)
 */
export function showLoadingToast(title: string, description?: string) {
  return toast({
    title: `⏳ ${title}`,
    description,
    duration: DURATION.PERSISTENT,
  });
}

/**
 * Common toast messages for CRUD operations
 */
export const TOAST_MESSAGES = {
  // Success messages
  CREATED: (resource: string) => showSuccessToast('Created', `${resource} created successfully.`),
  UPDATED: (resource: string) => showSuccessToast('Updated', `${resource} updated successfully.`),
  DELETED: (resource: string) => showSuccessToast('Deleted', `${resource} deleted successfully.`),
  SAVED: () => showSuccessToast('Saved', 'Changes saved successfully.'),
  COPIED: () => showSuccessToast('Copied', 'Copied to clipboard.'),

  // Error messages
  CREATE_FAILED: (resource: string) => showErrorToast('Error', `Failed to create ${resource}.`),
  UPDATE_FAILED: (resource: string) => showErrorToast('Error', `Failed to update ${resource}.`),
  DELETE_FAILED: (resource: string) => showErrorToast('Error', `Failed to delete ${resource}.`),
  LOAD_FAILED: (resource: string) => showErrorToast('Error', `Failed to load ${resource}.`),

  // Warning messages
  UNSAVED_CHANGES: () =>
    showWarningToast('Unsaved Changes', 'You have unsaved changes that will be lost.'),
  OFFLINE: () => showWarningToast('Offline', 'You are currently offline.'),
};
